<script setup>
import { computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'info',
    validator: (value) => ['success', 'error', 'warning', 'info', 'pending', 'accepted', 'completed', 'rejected', 'cancelled'].includes(value)
  },
  title: {
    type: String,
    required: true
  },
  message: {
    type: String,
    default: ''
  },
  duration: {
    type: Number,
    default: 5000 // 5 seconds
  },
  closable: {
    type: Boolean,
    default: true
  },
  icon: {
    type: String,
    default: ''
  },
  showProgress: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['close', 'click']);

const toastClasses = computed(() => {
  const classes = ['pz-toast'];
  classes.push(`pz-toast-${props.variant}`);
  return classes.join(' ');
});

const defaultIcons = {
  success: 'las la-check-circle',
  error: 'las la-exclamation-circle',
  warning: 'las la-exclamation-triangle',
  info: 'las la-info-circle',
  pending: 'las la-clock',
  accepted: 'las la-check-circle',
  completed: 'las la-check-double',
  rejected: 'las la-times-circle',
  cancelled: 'las la-ban'
};

const toastIcon = computed(() => {
  return props.icon || defaultIcons[props.variant] || defaultIcons.info;
});

let timer = null;

const handleClose = () => {
  if (timer) {
    clearTimeout(timer);
  }
  emit('close');
};

const handleClick = () => {
  emit('click');
};

onMounted(() => {
  if (props.duration > 0) {
    timer = setTimeout(() => {
      handleClose();
    }, props.duration);
  }
});

onUnmounted(() => {
  if (timer) {
    clearTimeout(timer);
  }
});
</script>

<template>
  <div 
    :class="toastClasses"
    @click="handleClick"
    role="alert"
  >
    <!-- Icon -->
    <div class="pz-toast-icon">
      <i :class="toastIcon"></i>
    </div>
    
    <!-- Content -->
    <div class="pz-toast-content">
      <h4 class="pz-toast-title">{{ title }}</h4>
      <p v-if="message" class="pz-toast-message">{{ message }}</p>
    </div>
    
    <!-- Close button -->
    <button 
      v-if="closable"
      @click.stop="handleClose"
      class="pz-toast-close"
      type="button"
      aria-label="Close"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
      </svg>
    </button>
    
    <!-- Progress bar -->
    <div 
      v-if="showProgress && duration > 0"
      class="pz-toast-progress"
      :style="`animation-duration: ${duration}ms`"
    ></div>
  </div>
</template>

<style scoped>
/* Toast styles are now imported globally via tailwind.css */

/* Fallback inline styles */
.pz-toast {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  min-width: 300px;
  background: white;
  border: 1px solid #e5e7eb;
}

.pz-toast:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.pz-toast-icon {
  flex-shrink: 0;
  font-size: 1.5rem;
  line-height: 1;
}

.pz-toast-content {
  flex: 1;
  min-width: 0;
}

.pz-toast-title {
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.pz-toast-message {
  font-size: 0.875rem;
  opacity: 0.9;
  word-break: break-word;
}

.pz-toast-close {
  flex-shrink: 0;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s;
  background: transparent;
  border: none;
  cursor: pointer;
}

.pz-toast-close:hover {
  background: rgba(0, 0, 0, 0.1);
}

.pz-toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 0.25rem;
  background: currentColor;
  opacity: 0.3;
  animation: toast-progress linear forwards;
}

@keyframes toast-progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Toast variants */
.pz-toast-success {
  background-color: #e0efdd;
  color: #465442;
  border-color: #c3d9c0;
}

.pz-toast-error {
  background-color: #fef2f2;
  color: #991b1b;
  border-color: #fecaca;
}

.pz-toast-warning {
  background-color: #fefce8;
  color: #854d0e;
  border-color: #fde68a;
}

.pz-toast-info {
  background-color: #eff6ff;
  color: #1e40af;
  border-color: #bfdbfe;
}

.pz-toast-pending {
  background-color: #ffeeea;
  color: var(--primary, #ef4444);
  border-color: #fecaca;
}

.pz-toast-accepted {
  background-color: #e0efdd;
  color: #465442;
  border-color: #c3d9c0;
}

.pz-toast-completed {
  background-color: #dae9f4;
  color: #495662;
  border-color: #bfdbfe;
}

.pz-toast-rejected {
  background-color: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

.pz-toast-cancelled {
  background-color: #f9fafb;
  color: #4b5563;
  border-color: #e5e7eb;
}
</style>